import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box } from "@mui/material";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import { StyledDateTextField, brandStandardUnderlineInputPropsSx } from "./style";

const PRIMARY_COLOR = "#AF3241";
const PRIMARY_COLOR_DARK = "#8a2833";

const TABBABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

/** Move focus to the next/previous tabbable outside pickers/modals that steal it. */
const focusAdjacentTabbable = (from, shiftKey) => {
  if (!from || typeof document === "undefined") return;
  const candidates = Array.from(document.querySelectorAll(TABBABLE_SELECTOR)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.tabIndex < 0) return false;
    // Skip the open picker list itself; do NOT skip .MuiModal-root — these
    // fields live inside dialogs and every in-dialog tab target would vanish.
    if (el.closest(".MuiPickersPopper-root")) return false;
    if (el.closest("[aria-hidden='true']")) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  });
  const index = candidates.indexOf(from);
  if (index === -1) return;
  const target = candidates[shiftKey ? index - 1 : index + 1];
  target?.focus();
};

const getSlotProps = (onOpenPicker, onBlurHandler, inputRef, fieldError, onKeyDownHandler, isOpen) => ({
  textField: {
    variant: "standard",
    fullWidth: true,
    autoComplete: "off",
    sx: {
      "& label + .MuiInput-root": { marginTop: "10px" },
      "& .MuiInput-input": { paddingTop: "6px", paddingBottom: "6px" },
    },
    onClick: onOpenPicker,
    onBlur: onBlurHandler,
    onKeyDown: onKeyDownHandler,
    inputRef: inputRef,
    error: fieldError !== "" && fieldError != null,
    helperText: fieldError !== "" && fieldError != null ? fieldError : "",
    FormHelperTextProps: {
      sx: {
        color: "#d32f2f",
        marginLeft: 0,
        fontSize: "0.75rem",
        // Wrap long messages under the field instead of widening it and pushing
        // the layout sideways.
        whiteSpace: "normal",
        overflowWrap: "break-word",
        lineHeight: 1.3,
      },
    },
    InputProps: {
      // Editable only while the picker is open — same as the date field, so
      // clicking a closed field opens the popper and typing only edits the
      // field's own sections once it is open.
      readOnly: !isOpen,
      sx: {
        ...brandStandardUnderlineInputPropsSx,
        cursor: isOpen ? "text" : "pointer",
        caretColor: isOpen ? PRIMARY_COLOR : "transparent",
        "& input": {
          cursor: isOpen ? "text" : "pointer",
          caretColor: isOpen ? PRIMARY_COLOR : "transparent",
          "&::selection": {
            backgroundColor: isOpen ? "rgba(175, 50, 65, 0.3)" : "transparent",
          },
        },
      },
    },
    InputLabelProps: {
      sx: {
        "&.Mui-focused": {
          color: PRIMARY_COLOR,
        },
      },
    },
  },
  digitalClockSectionItem: {
    sx: {
      fontSize: "0.85rem",
      height: 32,
      "&.Mui-selected": {
        backgroundColor: PRIMARY_COLOR,
        color: "#fff",
        "&:hover": { backgroundColor: PRIMARY_COLOR_DARK },
        "&:focus": { backgroundColor: PRIMARY_COLOR },
        "&.Mui-focusVisible": {
          backgroundColor: PRIMARY_COLOR,
          outline: `2px solid ${PRIMARY_COLOR_DARK}`,
          outlineOffset: -2,
        },
      },
      "&:hover": { backgroundColor: "rgba(175, 50, 65, 0.08)" },
      "&.Mui-focusVisible:not(.Mui-selected)": {
        outline: `2px solid ${PRIMARY_COLOR}`,
        outlineOffset: -2,
        backgroundColor: "rgba(175, 50, 65, 0.08)",
      },
    },
  },
  layout: {
    sx: {
      "& .MuiMultiSectionDigitalClock-root": {
        maxHeight: 220,
      },
      "& .MuiMultiSectionDigitalClockSection-root": {
        width: 56,
        // Drop MUI's bottom spacer so the list ends on the last time item.
        "&:after": { display: "none" },
      },
      "& .MuiPickersLayout-actionBar": {
        borderTop: "1px solid rgba(0,0,0,0.08)",
        padding: "4px 8px",
        justifyContent: "flex-end",
      },
    },
  },
  actionBar: {
    sx: {
      "& .MuiButton-root": {
        color: PRIMARY_COLOR,
        fontWeight: 600,
        fontSize: "0.8rem",
        minWidth: "auto",
        px: 1.5,
        "&:hover": {
          backgroundColor: "rgba(175, 50, 65, 0.08)",
        },
        "&:focus": {
          outline: `2px solid ${PRIMARY_COLOR}`,
          outlineOffset: 2,
        },
      },
    },
  },
  popper: {
    placement: "bottom-start",
    disableAutoFocus: true,
    // Without this, MUI restores focus to the field on close and fights Tab.
    disableRestoreFocus: true,
    modifiers: [{ name: "preventOverflow", options: { padding: 8 } }],
    sx: {
      zIndex: 1300,
      "& .MuiPaper-root": { boxShadow: "0 4px 20px rgba(0,0,0,0.15)" },
    },
  },
});

export default function TimePickerField({
  label,
  elemId,
  defaultVal,
  onChangeCall,
  fieldError,
  disabled,
}) {
  const [selectedTime, setSelectedTime] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [validationError, setValidationError] = useState("");
  const inputRef = useRef(null);
  const isClosingRef = useRef(false);
  // True while Tab is closing the picker — blocks blur/onClose from yanking focus
  // back onto this field after we have already moved it forward.
  const tabClosingRef = useRef(false);
  // Mirror of `isOpen` readable synchronously from callbacks/effects.
  const isOpenRef = useRef(false);
  // True while the field holds a partial, unparseable time (a section typed then
  // half-deleted). Distinct from a fully empty field.
  const hasInvalidDraftRef = useRef(false);

  const setOpen = useCallback((next) => {
    isOpenRef.current = next;
    setIsOpen(next);
  }, []);

  // Last value pushed to the parent. Guards against firing a redundant change —
  // in the store editor a change dispatches a full re-render, which mid-typing
  // would wipe the field.
  const lastEmittedRef = useRef(defaultVal || "");
  const emit = useCallback((val) => {
    if (val === lastEmittedRef.current) return;
    lastEmittedRef.current = val;
    onChangeCall(val);
  }, [onChangeCall]);

  // MUI's MultiSectionDigitalClockSection calls `activeItem.focus()` every time
  // the clock opens or a value is picked, which would steal focus from the text
  // field and send typed digits to the clock list. Patch focus() while the picker
  // is open to (a) no-op focus calls targeting the clock, so focus stays in the
  // field and typing edits it, and (b) always preventScroll for everything else —
  // the popper is portaled to <body>, so the default focus-scroll could shift the
  // page behind this modal. Mouse clicks on the clock still select, since their
  // click fires regardless of focus.
  const focusPatchRef = useRef(null);

  const installFocusPatch = useCallback(() => {
    if (focusPatchRef.current) return;
    const original = HTMLElement.prototype.focus;
    focusPatchRef.current = original;
    HTMLElement.prototype.focus = function patchedFocus(options) {
      if (typeof this.closest === "function" && this.closest(".MuiMultiSectionDigitalClock-root")) {
        return undefined;
      }
      return original.call(this, { ...(options || {}), preventScroll: true });
    };
  }, []);

  const removeFocusPatch = useCallback(() => {
    if (!focusPatchRef.current) return;
    HTMLElement.prototype.focus = focusPatchRef.current;
    focusPatchRef.current = null;
  }, []);

  useEffect(() => () => removeFocusPatch(), [removeFocusPatch]);

  // Sync the value in from the parent — but never while the picker is open. The
  // user is typing then, and a re-sync would wipe MUI's half-typed sections.
  useEffect(() => {
    if (isOpenRef.current) return;
    if (defaultVal) {
      setSelectedTime(moment(defaultVal, "HH:mm"));
    } else {
      setSelectedTime(null);
    }
    setValidationError("");
    hasInvalidDraftRef.current = false;
    lastEmittedRef.current = defaultVal || "";
  }, [defaultVal]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    installFocusPatch();
    setOpen(true);
  }, [disabled, installFocusPatch, setOpen]);

  // Once MUI mounts the popper, put focus on the text field so keystrokes edit it.
  // The focus patch keeps the digital clock from taking it back. Mirrors the date
  // picker's one-shot reclaim.
  useEffect(() => {
    if (!isOpen) return;
    const timerId = setTimeout(() => {
      if (!isClosingRef.current) inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timerId);
  }, [isOpen]);

  // Drop a half-typed value instead of silently reverting to the last valid one,
  // and flag the field so the parent's save is blocked and the user sees why.
  const validateInput = useCallback(() => {
    if (hasInvalidDraftRef.current) {
      hasInvalidDraftRef.current = false;
      setSelectedTime(null);
      setValidationError("Please enter a complete time (HH:MM)");
      emit("");
    }
  }, [emit]);

  // `blurInput` stays true for Enter / outside click so the field fully releases
  // focus. Tab leaves it false and sets tabClosingRef so a later onClose cannot
  // schedule blur/restore that pulls focus back to this field.
  const closePicker = useCallback((blurInput = true) => {
    isClosingRef.current = true;
    validateInput();
    setOpen(false);
    removeFocusPatch();
    window.getSelection()?.removeAllRanges();
    const shouldBlur = blurInput && !tabClosingRef.current;
    if (shouldBlur) {
      setTimeout(() => {
        inputRef.current?.blur();
      }, 100);
      setTimeout(() => {
        inputRef.current?.blur();
        isClosingRef.current = false;
      }, 300);
    } else {
      setTimeout(() => {
        isClosingRef.current = false;
        tabClosingRef.current = false;
      }, 300);
    }
  }, [validateInput, removeFocusPatch, setOpen]);

  const handleInputBlur = useCallback(() => {
    // A real exit runs through closePicker, which validates; nothing to do here.
  }, []);

  const handleTimeChange = (newValue) => {
    installFocusPatch();
    if (newValue === null) {
      // Fully cleared.
      hasInvalidDraftRef.current = false;
      setSelectedTime(null);
      setValidationError("");
      emit("");
      return;
    }
    if (!newValue.isValid()) {
      // Partial draft mid-typing: keep MUI's in-progress sections and don't touch
      // the parent, so typing never triggers a re-render that would wipe the
      // field. Flagged so closing the picker drops it rather than reverting to the
      // last valid value.
      hasInvalidDraftRef.current = true;
      return;
    }
    hasInvalidDraftRef.current = false;
    setSelectedTime(newValue);
    setValidationError("");
    emit(newValue.format("HH:mm"));
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (isOpen) closePicker(true);
      return;
    }
    // Tab: take over focus movement. Native Tab + MUI's restore-focus race and
    // sometimes snaps back to this field; preventDefault + explicit advance wins.
    if (e.key === "Tab" && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      tabClosingRef.current = true;
      const from = inputRef.current;
      const shiftKey = e.shiftKey;
      closePicker(false);
      focusAdjacentTabbable(from, shiftKey);
      const ensureMoved = () => {
        if (from && (document.activeElement === from || from.contains?.(document.activeElement))) {
          focusAdjacentTabbable(from, shiftKey);
        }
      };
      setTimeout(ensureMoved, 0);
      setTimeout(ensureMoved, 50);
      setTimeout(ensureMoved, 150);
    }
  }, [isOpen, closePicker]);

  const effectiveError = validationError || fieldError || "";
  const slotProps = getSlotProps(handleOpen, handleInputBlur, inputRef, effectiveError, handleKeyDown, isOpen);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box width="100%">
        <DesktopTimePicker
          label={label}
          value={selectedTime}
          onChange={handleTimeChange}
          open={isOpen}
          onOpen={handleOpen}
          onClose={() => closePicker(true)}
          minutesStep={5}
          ampm={false}
          format="HH:mm"
          disabled={disabled}
          slots={{ textField: StyledDateTextField }}
          slotProps={slotProps}
        />
      </Box>
    </LocalizationProvider>
  );
}
